import { useState, useCallback, useMemo } from "react";
import { Plus, Trash2, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

/**
 * Labor Section - handles labor entries
 * Includes table display and add/edit dialog
 * Automatically calculates hours based on estimated weeks
 */
export const LaborSection = ({
  laborEntries,
  employeeRoles = [], // Array of role name strings from settings
  estimatedWeeks,
  canManageJobs,
  formatCurrency,
  onAddEntry,
  onEditEntry,
  onDeleteEntry,
  laborSubtotal,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  const [form, setForm] = useState({
    role: "",
    hourly_rate: "",
    notes: "",
  });

  // Calculate hours based on estimated weeks (5 days × 8 hours/day)
  const calculatedHours = useMemo(() => {
    const weeks = parseFloat(estimatedWeeks) || 0;
    return weeks * 5 * 8; // 5 days per week, 8 hours per day
  }, [estimatedWeeks]);

  const resetForm = useCallback(() => {
    setForm({
      role: "",
      hourly_rate: "",
      notes: "",
    });
    setEditingEntry(null);
  }, []);

  const openDialog = useCallback((entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setForm({
        role: entry.role || "",
        hourly_rate: entry.hourly_rate?.toFixed(2) || "",
        notes: entry.notes || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  }, [resetForm]);

  const handleRoleSelect = useCallback((role) => {
    setForm(f => ({
      ...f,
      role,
    }));
  }, []);

  const handleSubmit = useCallback(() => {
    const hourlyRate = parseFloat(form.hourly_rate) || 0;
    const amount = hourlyRate * calculatedHours;

    const newEntry = {
      id: editingEntry?.id || crypto.randomUUID(),
      role: form.role,
      hourly_rate: hourlyRate,
      hours: calculatedHours,
      amount,
      notes: form.notes,
      description: `${form.role} - ${calculatedHours} hours @ ${formatCurrency(hourlyRate)}/hr`,
    };

    if (editingEntry) {
      onEditEntry(newEntry);
    } else {
      onAddEntry(newEntry);
    }

    setDialogOpen(false);
    resetForm();
  }, [form, calculatedHours, editingEntry, onAddEntry, onEditEntry, resetForm, formatCurrency]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl flex items-center gap-3 text-slate-700">
            <span>Labor</span>
            <span className="text-muted-foreground font-normal">&ndash;</span>
            <span className="text-xl font-normal text-slate-500">Subtotal</span>
            <span className="font-mono font-bold text-2xl text-slate-700" data-testid="labor-subtotal">{formatCurrency(laborSubtotal)}</span>
          </CardTitle>
          {canManageJobs && (
            <Button size="lg" className="text-lg px-6" onClick={() => openDialog()} data-testid="add-labor-entry-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Role
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {laborEntries.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>No labor entries yet</p>
              <p className="text-sm mt-1">Click &quot;Add Role&quot; to add labor by job role</p>
            </div>
          ) : (
            <div>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="bg-slate-100 border-b-2 border-slate-200">
                    <TableHead className="h-8 py-1 text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Role / Description</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Hours</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Hourly Rate</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Amount</TableHead>
                    {canManageJobs && <TableHead className="h-8 py-1 w-20"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {laborEntries.map((entry, idx) => (
                    <TableRow 
                      key={entry.id} 
                      className={
                        entry.is_material_labor ? "bg-blue-50/50 border-b border-slate-100" : 
                        entry.is_equipment_labor ? "bg-purple-50/50 border-b border-slate-100" : 
                        entry.is_misc_labor ? "bg-orange-50/50 border-b border-slate-100" : 
                        "border-b border-slate-100"
                      }
                    >
                      <TableCell className="py-3">
                        <div>
                          <span className="font-medium">{entry.role}</span>
                          {entry.is_material_labor && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">from Materials</span>
                          )}
                          {entry.is_equipment_labor && (
                            <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">from Equipment</span>
                          )}
                          {entry.is_misc_labor && (
                            <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">from Misc</span>
                          )}
                          {entry.notes && <p className="text-xs text-muted-foreground">{entry.notes}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{entry.hours > 0 ? entry.hours : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{entry.hourly_rate > 0 ? `${formatCurrency(entry.hourly_rate)}/hr` : "-"}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{formatCurrency(entry.amount)}</TableCell>
                      {canManageJobs && (
                        <TableCell>
                          {!entry.is_material_labor && !entry.is_equipment_labor && !entry.is_misc_labor && (
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(entry)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDeleteEntry(entry.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Labor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? "Edit" : "Add"} Labor Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {calculatedHours > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-800 font-medium">Auto-calculated Hours:</span>
                  <span className="text-lg font-bold text-blue-700">{calculatedHours} hrs</span>
                </div>
                <p className="text-xs text-blue-600 mt-1">Based on Est. Weeks ({estimatedWeeks} weeks × 5 days × 8 hrs)</p>
              </div>
            )}
            <div className="form-group">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={handleRoleSelect}>
                <SelectTrigger data-testid="labor-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {[...employeeRoles].sort((a, b) => a.localeCompare(b)).map(role => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {employeeRoles.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No roles found. Add roles in Settings &gt; Options.</p>
              )}
            </div>
            <div className="form-group">
              <Label>Hourly Rate ($) *</Label>
              <NumericInput 
                decimals={2}
                min={0}
                value={form.hourly_rate} 
                onChange={(e) => setForm(f => ({...f, hourly_rate: e.target.value}))} 
                data-testid="labor-rate-input"
              />
            </div>
            {form.hourly_rate && calculatedHours > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-800 font-medium">Total Labor Amount:</span>
                  <span className="text-lg font-bold text-green-700">
                    {formatCurrency((parseFloat(form.hourly_rate) || 0) * calculatedHours)}
                  </span>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  {formatCurrency(parseFloat(form.hourly_rate) || 0)}/hr × {calculatedHours} hrs
                </p>
              </div>
            )}
            <div className="form-group">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} 
                rows={2} 
              />
            </div>
            <Button 
              onClick={handleSubmit} 
              className="w-full" 
              disabled={!form.role || !form.hourly_rate || calculatedHours <= 0}
              data-testid="add-labor-submit-btn"
            >
              {editingEntry ? "Save Changes" : "Add Labor Entry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LaborSection;
